import { createSelector } from "reselect";
import { getQuestionStems } from "./questionStemSelectors";

const getAllQuestions = (state) => state.entities.questions || {};

const getAllLessons = (state) => state.entities.lessons || {};

const getCurrentLessonId = (state) => state.currentLessonId;

const getCurrentLessonById = createSelector(
  getAllLessons,
  getCurrentLessonId,
  (lessons, lessonId) => lessonId ? lessons[lessonId] : null
);

const calculateLessonDisplayIndex = (questionIndex, stemPosition, totalStems) => {
  if (questionIndex === 0) {
    if (totalStems > 1) {
      return `1.${stemPosition + 1}`;
    }
    return "1";
  }
  const questionNumber = questionIndex + 1;
  if (totalStems > 1) {
    return `${questionNumber}.${stemPosition + 1}`;
  }
  return String(questionNumber);
};

const buildEntriesForQuestion = (question, allStems, questionIndex) => {
  const stems = Object.values(allStems).filter((stem) => {
    const stemQuestionId = stem.questionId || stem.question_id;
    return stemQuestionId != null && Number(stemQuestionId) === Number(question.id);
  });
  const sortedStems = stems.sort((a, b) => a.position - b.position);
  const isInteractive = question?.format === "interactive";
  const questionNumber = questionIndex + 1;

  if (isInteractive) {
    const entries = [{
      question,
      stem: null,
      isFlow: true,
      questionIndex,
      displayIndex: String(questionNumber),
    }];
    sortedStems.forEach((stem) => {
      entries.push({
        question,
        stem,
        isFlow: false,
        questionIndex,
        displayIndex: `${questionNumber}.${stem.position + 1}`,
      });
    });
    return entries;
  }

  if (sortedStems.length > 0) {
    return sortedStems.map((stem) => ({
      question,
      stem,
      isFlow: false,
      questionIndex,
      displayIndex: calculateLessonDisplayIndex(questionIndex, stem.position, sortedStems.length),
    }));
  }

  return [{
    question,
    stem: null,
    isFlow: false,
    questionIndex,
    displayIndex: calculateLessonDisplayIndex(questionIndex, 0, 1),
  }];
};

export const getFlattenedLessonQuestions = createSelector(
  getCurrentLessonById,
  getAllQuestions,
  getQuestionStems,
  (lesson, allQuestions, allStems) => {
    if (!lesson) {
      return [];
    }
    if (!allQuestions || typeof allQuestions !== "object") {
      return [];
    }

    const flattened = [];
    let questionIndex = 0;

    const lookupQuestion = (questionId) =>
      allQuestions[questionId] ||
      allQuestions[String(questionId)] ||
      Object.values(allQuestions).find((q) => Number(q?.id) === Number(questionId));

    if (lesson.meta?.question) {
      const question = lookupQuestion(lesson.meta.question);
      if (question) {
        flattened.push(...buildEntriesForQuestion(question, allStems, questionIndex));
        questionIndex++;
      }
    }

    if (Array.isArray(lesson.meta?.questions)) {
      lesson.meta.questions.forEach((questionId) => {
        const question = lookupQuestion(questionId);
        if (question) {
          flattened.push(...buildEntriesForQuestion(question, allStems, questionIndex));
          questionIndex++;
        }
      });
    }

    return flattened;
  }
);

const getCurrentLessonQuestionIndex = (state) => state.lesson.currentQuestionIndex;

export const getCurrentLessonQuestionAndStem = createSelector(
  getFlattenedLessonQuestions,
  getCurrentLessonQuestionIndex,
  (flattenedQuestions, currentIndex) => {
    const current = flattenedQuestions[currentIndex];
    if (!current) return { question: null, stem: null, isFlow: false, displayIndex: null };

    return {
      question: current.question,
      stem: current.stem,
      isFlow: !!current.isFlow,
      questionIndex: current.questionIndex,
      displayIndex: current.displayIndex,
    };
  }
);
